import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixAchievementsCategoriesV3CategoriesServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'bo._base_domain_': [
      {
        srcPath: '/program-categories-service',
        destPath: '',
      },
    ],
    'wixbo.ai': [
      {
        srcPath: '/program-categories-service',
        destPath: '',
      },
    ],
    'wix-bo.com': [
      {
        srcPath: '/program-categories-service',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/program-categories-service',
        destPath: '',
      },
      {
        srcPath: '/program-categories-service',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/program-categories-service',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/program-categories-service',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/program-categories-service',
        destPath: '',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/program-categories-service',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/program-categories-service',
        destPath: '',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/program-categories-service',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/program-categories-service',
        destPath: '',
      },
      {
        srcPath: '/program-categories-service',
        destPath: '',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/program-categories-service',
        destPath: '',
      },
      {
        srcPath: '/program-categories-service',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/_api/program-categories-service',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_online-programs_categories';

/** Creates a new Category */
export function createCategory(payload: object): RequestOptionsFactory<any> {
  function __createCategory({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'category.createdDate' },
          { path: 'category.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.achievements.categories.v3.category',
      method: 'POST' as any,
      methodFqn:
        'wix.achievements.categories.v3.CategoriesService.CreateCategory',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAchievementsCategoriesV3CategoriesServiceUrl({
        protoPath: '/v3/categories',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'category.createdDate' },
              { path: 'category.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createCategory;
}

/** Creates new categories in a bulk */
export function bulkCreateCategory(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkCreateCategory({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'categories.createdDate' },
          { path: 'categories.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.achievements.categories.v3.category',
      method: 'POST' as any,
      methodFqn:
        'wix.achievements.categories.v3.CategoriesService.BulkCreateCategory',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAchievementsCategoriesV3CategoriesServiceUrl({
        protoPath: '/v3/bulk/categories/create',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkCreateCategory;
}

/** Get a Category by id */
export function getCategory(payload: object): RequestOptionsFactory<any> {
  function __getCategory({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.achievements.categories.v3.category',
      method: 'GET' as any,
      methodFqn: 'wix.achievements.categories.v3.CategoriesService.GetCategory',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAchievementsCategoriesV3CategoriesServiceUrl({
        protoPath: '/v3/categories/{categoryId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'category.createdDate' },
              { path: 'category.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getCategory;
}

/** Get list of categories by list of ids */
export function listCategories(payload: object): RequestOptionsFactory<any> {
  function __listCategories({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.achievements.categories.v3.category',
      method: 'GET' as any,
      methodFqn:
        'wix.achievements.categories.v3.CategoriesService.ListCategories',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAchievementsCategoriesV3CategoriesServiceUrl({
        protoPath: '/v3/categories',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'categories.createdDate' },
              { path: 'categories.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listCategories;
}

/**
 * Update a Category, supports partial update
 * Pass the latest `revision` for a successful update
 */
export function updateCategory(payload: object): RequestOptionsFactory<any> {
  function __updateCategory({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'mask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'category.createdDate' },
          { path: 'category.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.achievements.categories.v3.category',
      method: 'PATCH' as any,
      methodFqn:
        'wix.achievements.categories.v3.CategoriesService.UpdateCategory',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAchievementsCategoriesV3CategoriesServiceUrl({
        protoPath: '/v3/categories/{category.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'category.createdDate' },
              { path: 'category.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateCategory;
}

/** Delete a Category */
export function deleteCategory(payload: object): RequestOptionsFactory<any> {
  function __deleteCategory({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.achievements.categories.v3.category',
      method: 'DELETE' as any,
      methodFqn:
        'wix.achievements.categories.v3.CategoriesService.DeleteCategory',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAchievementsCategoriesV3CategoriesServiceUrl({
        protoPath: '/v3/categories/{categoryId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteCategory;
}

/** Query Categories using [WQL - Wix Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language) */
export function queryCategories(payload: object): RequestOptionsFactory<any> {
  function __queryCategories({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.achievements.categories.v3.category',
      method: 'POST' as any,
      methodFqn:
        'wix.achievements.categories.v3.CategoriesService.QueryCategories',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAchievementsCategoriesV3CategoriesServiceUrl({
        protoPath: '/v3/categories/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'categories.createdDate' },
              { path: 'categories.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryCategories;
}

/** Assign or unassign categories to a program */
export function bulkAssignCategoriesToProgram(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkAssignCategoriesToProgram({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.achievements.categories.v3.category',
      method: 'POST' as any,
      methodFqn:
        'wix.achievements.categories.v3.CategoriesService.BulkAssignCategoriesToProgram',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAchievementsCategoriesV3CategoriesServiceUrl({
        protoPath: '/v3/categories/bulk-assign-to-program',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkAssignCategoriesToProgram;
}
